import { env } from '@/config/env'

export type TenantGrant = {
  tenantId: string
  name: string
  roles: string[]
  permissions: string[]
}

export type AuthLoginPrincipal = {
  realm: 'talent'
  userId: string
  grants: TenantGrant[]
}

/**
 * `principal` on every one of login/session/refresh: `issueSession()` on the gateway is the
 * shared tail of all three, and refresh re-reads it fresh at rotation (the moment a ban or
 * membership revocation actually takes effect). Not optional — there is no session response
 * without one.
 */
export interface PlatformSession {
  accessToken: string
  refreshToken: string
  /** Lifetime of the access token, in seconds. */
  expiresIn: number
  principal: AuthLoginPrincipal
}

export class AuthApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message)
    this.name = 'AuthApiError'
  }
}

/**
 * The gateway owns sign-in end to end for talent too: the FE posts credentials, never Firebase
 * itself. Same `/auth/login` core-service's recruiter login uses, `realm: 'talent'` is the only
 * difference — `realmClient()` in the gateway picks talent-service from that field alone.
 */
export async function loginTalent(email: string, password: string) {
  let session: PlatformSession

  try {
    session = await authRequest<PlatformSession>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, realm: 'talent' }),
    })
  } catch (err) {
    if (err instanceof AuthApiError && err.status === 401) {
      throw new AuthApiError('Email or password are not valid', 401, err.code)
    }
    throw err
  }

  if (!isTalentLoginSession(session)) {
    throw new AuthApiError('Authentication service returned an invalid session.', 200)
  }

  return session
}

/**
 * Talent's self-serve counterpart to `loginTalent` — `/auth/signup-talent` on the gateway (no
 * signup token: a talent account is created, not invited, see that route's own comment). Ends
 * signed in on the SAME session shape login returns, so the caller applies it identically.
 */
export async function signupTalent(email: string, password: string, name?: { first?: string; last?: string }) {
  let session: PlatformSession

  try {
    session = await authRequest<PlatformSession>('/auth/signup-talent', {
      method: 'POST',
      body: JSON.stringify({ email, password, ...(name ? { name } : {}) }),
    })
  } catch (err) {
    if (err instanceof AuthApiError && err.status === 409) {
      throw new AuthApiError('An account with this email already exists — try logging in instead.', 409, err.code)
    }
    throw err
  }

  if (!isTalentLoginSession(session)) {
    throw new AuthApiError('Authentication service returned an invalid session.', 200)
  }

  return session
}

/** `/claim/:token`'s read side — never consumes the token. See the gateway route's own comment. */
export function getClaimInfo(token: string) {
  return authRequest<{ email: string; firstName?: string; lastName?: string; hasAccount: boolean }>(
    `/auth/claim/${encodeURIComponent(token)}`,
    {},
  )
}

/**
 * `/claim/:token`'s write side. `password` is required only when the claim info said
 * `hasAccount: false` — the page enforces that before calling this, this just passes through
 * whatever it was given. Ends signed in on the same session shape login/signup return.
 */
export async function redeemClaim(token: string, input: { password?: string; name?: { first?: string; last?: string } }) {
  let session: PlatformSession

  try {
    session = await authRequest<PlatformSession>(`/auth/claim/${encodeURIComponent(token)}`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  } catch (err) {
    if (err instanceof AuthApiError && err.status === 404) {
      throw new AuthApiError('This claim link is invalid or has already been used.', 404, err.code)
    }
    if (err instanceof AuthApiError && err.status === 409) {
      throw new AuthApiError('An account with this email already exists — try logging in instead.', 409, err.code)
    }
    throw err
  }

  if (!isTalentLoginSession(session)) {
    throw new AuthApiError('Authentication service returned an invalid session.', 200)
  }

  return session
}

export function requestTalentPasswordReset(email: string) {
  return authRequest<{ ok: true }>('/auth/password-reset', {
    method: 'POST',
    body: JSON.stringify({ email, realm: 'talent' }),
  })
}

export function verifyTalentPasswordResetCode(oobCode: string) {
  return authRequest<{ email: string }>('/auth/password-reset/verify', {
    method: 'POST',
    body: JSON.stringify({ oobCode, realm: 'talent' }),
  })
}

export function confirmTalentPasswordReset(oobCode: string, newPassword: string) {
  return authRequest<{ email: string }>('/auth/password-reset/confirm', {
    method: 'POST',
    body: JSON.stringify({ oobCode, newPassword, realm: 'talent' }),
  })
}

/**
 * `POST /auth/password` — the ONE auth-client call that needs a bearer token: every other call
 * here happens before a session exists (login, signup, reset). This is a signed-in action, so it
 * carries the caller's own access token explicitly (this module has no ambient access to
 * `api-client.ts`'s attach-the-token plumbing, by design — auth and the BFF's API surface are
 * deliberately separate clients).
 */
export async function changePassword(accessToken: string, currentPassword: string, newPassword: string) {
  return authRequest<{ ok: true; sessionsRevoked: number; reloginRequired: true }>('/auth/password', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ currentPassword, newPassword }),
  })
}

/**
 * The sign-in address lives in Firebase; the gateway changes both halves and revokes the sessions
 * (legacy's talent-profile-ms synced Firebase on email change — the port had left Firebase stale).
 */
export async function changeEmail(accessToken: string, email: string) {
  return authRequest<{ ok: true; email: string; sessionsRevoked: number; reloginRequired: true }>('/auth/me/email', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ email }),
  })
}

export function createPlatformSession(idToken: string) {
  return authRequest<PlatformSession>('/auth/session', {
    method: 'POST',
    body: JSON.stringify({ idToken, realm: 'talent' }),
  })
}

export function rotatePlatformSession(refreshToken: string) {
  return authRequest<PlatformSession>('/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
  })
}

export async function revokePlatformSession(refreshToken: string) {
  await authRequest<void>('/auth/logout', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
  })
}

async function authRequest<T>(path: string, init: RequestInit): Promise<T> {
  let response: Response

  try {
    response = await fetch(`${env.authApiUrl}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...init.headers,
      },
    })
  } catch {
    throw new AuthApiError('Authentication service is unavailable. Please try again.', 0)
  }

  if (!response.ok) {
    const error = (await response.json().catch(() => null)) as
      | { error?: string; message?: string; code?: string }
      | null
    throw new AuthApiError(
      error?.message ?? error?.error ?? 'Authentication failed. Please try again.',
      response.status,
      error?.code,
    )
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

function isTalentLoginSession(value: unknown): value is PlatformSession {
  if (!isRecord(value)) return false
  if (typeof value.accessToken !== 'string' || typeof value.refreshToken !== 'string') return false
  if (typeof value.expiresIn !== 'number' || value.expiresIn <= 0) return false
  if (!isRecord(value.principal)) return false

  return value.principal.realm === 'talent' && typeof value.principal.userId === 'string' && Array.isArray(value.principal.grants)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
